/**
 * UserSelectItemContainer - Wraps user select row with stable callback
 *
 * Binds the userId to the onToggleUser callback via useCallback to prevent
 * inline function creation in the parent list.
 */

import { useCallback } from "react";

import UserSelectItemView from "./UserSelectItemView";

/** Props for UserSelectItemContainer */
interface UserSelectItemContainerProps {
  /** The user's unique ID */
  readonly userId: string;
  /** Display name for the user */
  readonly displayName: string;
  /** Initials for the avatar circle */
  readonly initials: string;
  /** Job title or role */
  readonly title: string;
  /** Whether this user is currently selected */
  readonly isSelected: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Callback when the user row is toggled */
  readonly onToggleUser: (userId: string) => void;
}

/**
 * Container that binds userId to the toggle handler for a user select row
 * @param props - Component props
 * @param props.userId - The user's unique ID
 * @param props.displayName - Display name for the user
 * @param props.initials - Initials for the avatar circle
 * @param props.title - Job title or role
 * @param props.isSelected - Whether this user is currently selected
 * @param props.isDark - Whether dark mode is active
 * @param props.onToggleUser - Callback when the user row is toggled
 */
const UserSelectItemContainer = ({
  userId,
  displayName,
  initials,
  title,
  isSelected,
  isDark,
  onToggleUser,
}: UserSelectItemContainerProps) => {
  const handlePress = useCallback(() => {
    onToggleUser(userId);
  }, [onToggleUser, userId]);

  return (
    <UserSelectItemView
      displayName={displayName}
      initials={initials}
      title={title}
      isSelected={isSelected}
      isDark={isDark}
      onPress={handlePress}
    />
  );
};

UserSelectItemContainer.displayName = "UserSelectItemContainer";

export default UserSelectItemContainer;
